import templateUrl from './onboarding-policy-editor.html'

/**
 * An onboarding policy editor that allows the policy to be edited
 * as a single form entity.
 *
 * This is not safe for editing onboarding policies in place; all edited policies
 * should be saved as a new onboarding policy entity on the backend.
 */
export const OnboardingPolicyEditorComponent = {
    bindings: {
        onboardingPolicy: "<"
    },
    controller: OnboardingPolicyEditorController,
    controllerAs: "vm",
    require: {
        editForm: "?^^form"
    },
    templateUrl
}

function OnboardingPolicyEditorController(utilityService) {
    'ngInject'
    const vm = this;

    vm.$onInit = onInit;
    vm.editForm; // Optional requirement.
    vm.onAddApplicationStatus = onAddApplicationStatus;
    vm.onAddDocument = onAddDocument;
    vm.onAddDocumentGroup = onAddDocumentGroup;
    vm.onAddReferenceField = onAddReferenceField;
    vm.onClearBlankForm = onClearBlankForm;
    vm.onClearRequirementsFile = onClearRequirementsFile;
    vm.onMoveApplicationStatus = onMoveApplicationStatus;
    vm.onMoveDocument = onMoveDocument;
    vm.onMoveDocumentGroup = onMoveDocumentGroup;
    vm.onMoveReferenceField = onMoveReferenceField;
    vm.onRemoveApplicationStatus = onRemoveApplicationStatus;
    vm.onRemoveDocument = onRemoveDocument;
    vm.onRemoveDocumentGroup = onRemoveDocumentGroup;
    vm.onRemoveReferenceField = onRemoveReferenceField;
    vm.onboardingPolicy;
    vm.statusTypes;

    function onInit() {
        vm.statusTypes = [
            "Initial",
            "Hidden",
            "Approved",
            "Terminated",
            "Incomplete",
            "Suspended",
            "SelfSignUp"
        ];
        vm.onboardingPolicy.OnboardingDocuments.forEach(function (document) {
            document.allowedFileTypeOptions = createMultiInputOptions(function () {
                return document.AllowedFileTypes;
            });
        });
        vm.contactEmailOptions = createMultiInputOptions(function () {
            return vm.onboardingPolicy.ContactEmails;
        });
    }

    function onAddApplicationStatus() {
        vm.onboardingPolicy.ApplicationStatuses.push({});
    }

    function onAddDocument() {
        const document = {
            AllowedFileTypes: []
        };
        document.allowedFileTypeOptions = createMultiInputOptions(function () {
            return document.AllowedFileTypes;
        });
        vm.onboardingPolicy.OnboardingDocuments.push(document);
    }

    function onAddDocumentGroup() {
        vm.onboardingPolicy.OnboardingDocumentGroups.push({});
    }

    function onAddReferenceField() {
        vm.onboardingPolicy.ReferenceFields.push({
            ValueType: "String",
            ShowInGrid: true
        });
    }

    function onClearBlankForm(document) {
        document.BlankFormUpload = null;
    }

    function onClearRequirementsFile(documentGroup) {
        documentGroup.RequirementsFileUpload = null;
    }

    function onRemoveApplicationStatus(applicationStatus) {
        vm.onboardingPolicy.ApplicationStatuses = vm.onboardingPolicy.ApplicationStatuses.filter(function (status) {
            return status !== applicationStatus;
        });
    }

    function onRemoveDocument(document) {
        vm.onboardingPolicy.OnboardingDocuments = vm.onboardingPolicy.OnboardingDocuments.filter(function (p) {
            return p !== document;
        });
    }

    function onRemoveDocumentGroup(documentGroup) {
        vm.onboardingPolicy.OnboardingDocumentGroups = vm.onboardingPolicy.OnboardingDocumentGroups
            .filter(p => p !== documentGroup)
    }

    function onRemoveReferenceField(referenceField) {
        vm.onboardingPolicy.ReferenceFields = vm.onboardingPolicy.ReferenceFields.filter(function (p) {
            return p !== referenceField;
        });
    }

    function onMoveApplicationStatus(status, index) {
        moveItem(vm.onboardingPolicy.ApplicationStatuses, status, index);
    }

    function onMoveReferenceField(field, index) {
        moveItem(vm.onboardingPolicy.ReferenceFields, field, index);
    }

    function onMoveDocumentGroup(group, index) {
        moveItem(vm.onboardingPolicy.OnboardingDocumentGroups, group, index);
    }

    function onMoveDocument(document, index) {
        moveItem(vm.onboardingPolicy.OnboardingDocuments, document, index);
    }

    /**
     * Move the given item within the list of items to its new index, in place.
     * @param {T[]} items
     * @param {T} movedItem
     * @param {number} index
     * @template T
     */
    function moveItem(items, movedItem, index) {
        return utilityService.moveItem(items, movedItem, index);
    }

    /**
     * Create kendo-multi-select options for the data source returned from the sourceFn
     * @param {any} sourceFn A function that returns an array representing data selected
     * in the multi-select.
     */
    function createMultiInputOptions(sourceFn) {
        // Return options that perform "server filtering" by enabling the filter
        // and auto-adding the filter text to the list of potential results.
        return {
            filters: "contains",
            dataTextField: "value",
            dataValueField: "value",
            valuePrimitive: true,
            autoClose: false,
            dataSource: {
                transport: {
                    read: function (options) {
                        const results = sourceFn().map(function (item) {
                            return {
                                value: item
                            };
                        });
                        if (options.data && options.data.filter && options.data.filter.filters
                            && options.data.filter.filters[0]) {
                            // Add filter text to the results.
                            results.unshift({
                                value: options.data.filter.filters[0].value
                            });
                        }
                        options.success(results);
                    }
                },
                serverFiltering: true,
                serverPaging: true,
                pageSize: 30
            }
        };
    }
}
